import { Alert, Box } from "@hightouch/htui";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type { FeatureMatrixFormValues } from "src/pages/decision-engines/configuration/feature-matrices/types";
import { OVERALL_COUNT_KEY } from "src/pages/decision-engines/configuration/feature-matrices/utils";

type Props = {
  /** Relationship columns as last saved, used to tell a new column from an existing one. */
  savedRelationships: FeatureMatrixFormValues["relationships"];
};

/**
 * Warns that flagging a feature the matrix has been generating all along reaches
 * backwards, into values already stored for every customer who revoked consent.
 * Flagging a brand-new column has no history to purge, so it says nothing.
 */
export function ConsentHistoryAlert({ savedRelationships }: Props) {
  const { control } = useFormContext<FeatureMatrixFormValues>();
  const relationships = useWatch({ control, name: "relationships" });
  const consentPurge = useWatch({ control, name: "consentPurge" });
  const [dismissed, setDismissed] = useState<string | null>(null);

  const newlyFlagged: string[] = [];
  for (const [key, columnMap] of Object.entries(relationships ?? {})) {
    const saved = savedRelationships[key] ?? {};
    for (const [columnName, config] of Object.entries(columnMap)) {
      if (columnName === OVERALL_COUNT_KEY) continue;
      if (!config.sanitizeOnConsentRevocation) continue;
      const savedConfig = saved[columnName];
      if (!savedConfig || savedConfig.sanitizeOnConsentRevocation) continue;
      newlyFlagged.push(columnName);
    }
  }

  const signature = newlyFlagged.join(",");
  if (newlyFlagged.length === 0 || dismissed === signature) {
    return null;
  }

  const message = !consentPurge
    ? "Flagging an existing feature covers its stored values for every customer who revoked consent. Nothing is removed until you link a revocations model in consent settings."
    : consentPurge.mode === "execute"
      ? "Flagging an existing feature removes its stored values for every customer who revoked consent, starting on the next daily run."
      : "Flagging an existing feature covers its stored values for every customer who revoked consent. Consent settings are set to report only, so no run removes them until you change that.";

  return (
    <Box px={6} pb={2}>
      <Alert
        variant="inline"
        type="warning"
        title={
          newlyFlagged.length === 1
            ? `${newlyFlagged[0]} is marked for sanitization`
            : `${newlyFlagged.length} existing features are marked for sanitization`
        }
        message={message}
        onDismiss={() => setDismissed(signature)}
      />
    </Box>
  );
}
